/**
 * The frame around every page: a link that skips to the content, the header, the page itself
 * and the footer, all in the reader's light or dark theme. The app's pages show a soft glow behind
 * their headings (the brand's colours on the light theme, light from above on the dark one). The
 * public pages (the home page and the guides) open on their own aurora instead.
 *
 * A new page opens at its top, a link to a part of a page goes to that part, and going back
 * returns to where the reader was (the router's `ScrollRestoration`).
 */

import { Outlet, ScrollRestoration, useLocation } from "react-router";

import { useEndSessionOnAccountSwitch, useWalletDataReset } from "../auth/session.js";
import { useThemeSync } from "../theme.js";
import { SiteFooter } from "./SiteFooter.js";
import { SiteHeader } from "./SiteHeader.js";

/** The public pages, which open on their own aurora. */
const PUBLIC_PAGES = new Set(["/", "/brands", "/creators"]);

/**
 * The glow at the top of the app's pages. On the light theme: the brand's colours over a fading
 * grid. On the dark theme: a faint light from above with a trace of blue, and no grid, so that
 * the page's cards and figures stand out instead of the backdrop.
 */
function Backdrop() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[32rem] overflow-hidden"
    >
      <div className="bg-grid absolute inset-0 opacity-80 dark:hidden" />
      <div className="absolute -top-64 left-1/2 h-[32rem] w-[72rem] max-w-[180vw] -translate-x-1/2 rounded-full bg-gradient-to-r from-brand-300/25 via-brand-200/20 to-brand-400/20 blur-3xl dark:from-brand-400/[0.05] dark:via-white/[0.03] dark:to-brand-400/[0.04]" />
      <div className="bg-spotlight absolute inset-0 hidden dark:block" />
    </div>
  );
}

export function Layout() {
  useEndSessionOnAccountSwitch();
  useWalletDataReset();
  useThemeSync();
  const { pathname } = useLocation();
  const publicPage = PUBLIC_PAGES.has(pathname);

  return (
    <div className="flex min-h-dvh flex-col bg-zinc-50 text-zinc-900 dark:bg-ink-950 dark:text-zinc-100">
      <a
        href="#content"
        className="sr-only z-50 rounded-lg bg-white px-4 py-2 text-sm font-medium shadow focus:not-sr-only focus:fixed focus:top-3 focus:left-3 dark:bg-ink-900"
      >
        Skip to content
      </a>
      <SiteHeader overHero={publicPage} />
      <main id="content" className="relative isolate flex-1">
        {!publicPage && <Backdrop />}
        <Outlet />
      </main>
      <SiteFooter />
      <ScrollRestoration />
    </div>
  );
}
